import { ReactElement } from 'react';
import { InputFieldWithTitles } from '@UI-Elements/Select/InputFieldWithTitles';
import { IRoute } from '@sp-types/index';
import React from 'react';

export const routeColors = ['#5467b2', '#7ea4ff', '#19ceb8', '#99cdd8'];

// beautiful code :-(
export const calcRouteColor = (routeId: string): string => routeColors[parseInt(routeId[routeId.length - 1]) - 1];

export const sortByOrder = (a: { order: number }, b: { order: number }) => a.order - b.order;

export function reorderList<T extends { id: string; order: number }>(
  itemList: T[],
  itemAId: string,
  itemBId: string,
): T[] {
  const itemsMap = new Map(itemList.map((item) => [item.id, item]));
  const itemA = itemsMap.get(itemAId);
  const itemB = itemsMap.get(itemBId);

  if (!itemA || !itemB) {
    return itemList;
  }

  const isForward = itemA.order < itemB.order;
  const [startOrder, endOrder] = isForward ? [itemA.order, itemB.order] : [itemB.order, itemA.order];

  return itemList
    .map((item) => {
      if (item.id === itemAId) {
        return { ...item, order: itemB.order };
      } else if (item.id === itemBId && !isForward) {
        return { ...item, order: itemB.order + 1 };
      } else if (item.order > startOrder && item.order <= endOrder) {
        return { ...item, order: isForward ? item.order - 1 : item.order + 1 };
      }
      return item;
    })
    .sort((a, b) => a.order - b.order);
}

export const addColor = (route: IRoute, index: number): IRoute => ({
  ...route,
  color: routeColors[index],
  routeId: 'route' + (index + 1),
});

export function getItemsOfMaterial<T extends { materialId: string }>(items: T[], materialId: string): T[] {
  return items.filter((item) => item.materialId === materialId);
}

export const getStartDate = (monthsCount: number): Date => {
  const today = new Date();

  const fromMonth = today.getMonth() - (monthsCount % 12);
  const fromYear = today.getFullYear() - monthsCount / 12;

  return new Date(fromYear, fromMonth, today.getDate());
};

export function sliceFromDate<T extends { date?: string; d?: string }>(items: T[], fromDate: Date): T[] {
  return items.filter(({ date, d }) => new Date(d || date || '') >= fromDate);
}

export const HeaderSubLabel: React.FC<{
  line1: string;
  subline: string | ReactElement;
  line2: string | ReactElement;
  border?: boolean;
}> = ({ line1, line2, subline, border = false }) => (
  <div className={`flex flex-col gap-2 text-base w-40 ${border ? 'border-l-2 border-l-gray-100 ps-3' : ''}`}>
    <span className="">{line1}</span>
    <span className="text-gray-300">{subline}</span>
    <span className="">{line2}</span>
  </div>
);

export const ExposureZone: React.FC<{
  title: string;
  above: number;
  below: number;
  disabled?: boolean;
  setAbove: (value: number) => void;
  setBelow: (value: number) => void;
}> = ({ title, above, below, setAbove, setBelow, disabled }) => (
  <div className="flex flex-col gap-3 text-base">
    <p className="text-lg">{title}</p>
    <span className="text-gray-700 ps-3 flex flex-col gap-3 text-base">
      <span className="text-gray-450">Price (in millions)</span>
      <span className="flex gap-2">
        <p className="w-14">Above</p>
        <div className="w-20">
          <InputFieldWithTitles
            value={above}
            changeValueFunc={(val) => setAbove(+val)}
            isNumeric={true}
            disabled={disabled}
          />
        </div>
      </span>
      <span className="flex gap-2">
        <p className="w-14">Below</p>
        <div className="w-20">
          <InputFieldWithTitles
            value={below}
            changeValueFunc={(val) => setBelow(+val)}
            isNumeric={true}
            disabled={disabled}
          />
        </div>
      </span>
    </span>
  </div>
);

export const generateDangerZone = (warnFrom: number, warnTo: number): number =>
  Math.max(30, 10 * Math.ceil(0.3 * ((Math.abs(warnFrom) + warnTo) / 2)));

export const PricingSkeleton: React.FC = () => (
  <div className="flex flex-col p-3 gap-3">
    <div className="flex justify-between gap-4">
      <div className="h-8 w-64 bg-gray-100 animate-pulse rounded-xl"></div>
      <div className="flex flex-1">&nbsp;</div>
      <div className="h-8 w-48 bg-gray-100 animate-pulse rounded-xl"></div>
      <div className="h-8 w-32 bg-gray-100 animate-pulse rounded-xl"></div>
    </div>
    <div className="flex flex-row gap-4">
      <div className="flex h-72 flex-1 bg-gray-100 animate-pulse rounded-xl"></div>
      <div className="flex h-72 w-40 bg-gray-100 animate-pulse rounded-xl"></div>
    </div>
  </div>
);
